import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, TicketPercent, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { discountEffectiveState, discountsClient, type Discount } from '@/lib/discounts-client';
import type { CartView } from '@/lib/draft-order-client';

interface CartCouponSectionProps {
  /** The saved cart — a coupon lives on the server order, never in the staged working copy. */
  cart: CartView;
  editable: boolean;
  /**
   * Staged edits are pending. Applying then would race the save the operator still has to do — an
   * ITEM_DISCOUNT coupon can add reward lines the staged snapshot knows nothing about, and the save
   * would silently take them off again.
   */
  locked: boolean;
  busy: boolean;
  /** Resolves true when the coupon went on — the input clears only then. */
  onApply: (code: string) => Promise<boolean>;
  onRemove: () => void;
}

/**
 * Apply or remove a coupon on the cart — the operator-side counterpart of the customer typing a
 * code at the agent, going through the same backend use-case and the same refusals.
 *
 * The free-text input is the primary path (a code from a campaign the operator may not have open);
 * the dropdown lists the brand's currently-active coded coupons so the common case is one click.
 */
export function CartCouponSection({ cart, editable, locked, busy, onApply, onRemove }: CartCouponSectionProps) {
  const { t } = useTranslation('conversations');
  const [code, setCode] = useState('');

  const applied = cart.appliedDiscount;

  // Only fetched while the control is actually usable — and never for the read-only panel, where
  // the totals row already tells the whole coupon story.
  const { data: available = [] } = useQuery({
    queryKey: ['cart-coupons', cart.brandId],
    queryFn: async () => (await discountsClient.list()).data,
    enabled: editable && !applied,
    staleTime: 60_000,
    select: (coupons) => coupons.filter((coupon) => isApplicableTo(coupon, cart)),
  });

  if (!editable) return null;

  const disabled = locked || busy;

  const submit = async () => {
    const trimmed = code.trim();
    if (!trimmed || disabled) return;
    if (await onApply(trimmed)) setCode('');
  };

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('cart.coupon.title')}</h3>

      {applied ? (
        <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm">
          <TicketPercent className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-foreground">{applied.code ?? applied.name}</p>
            {applied.minimumOrderAmount != null && (
              <p className="text-xs text-muted-foreground">
                {t('cart.coupon.minimum', {
                  amount: `${cart.currencySymbol}${applied.minimumOrderAmount.toFixed(2)}`,
                })}
              </p>
            )}
          </div>
          {/* An automatic promotion or the POS's own discount — nobody typed it, nobody removes it. */}
          {!applied.auto && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              disabled={disabled}
              onClick={onRemove}
              aria-label={t('cart.coupon.remove')}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="flex gap-2">
            <Input
              value={code}
              disabled={disabled}
              placeholder={t('cart.coupon.placeholder')}
              className="uppercase placeholder:normal-case"
              onChange={(event) => setCode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void submit();
              }}
            />
            <Button
              variant="outline"
              loading={busy}
              disabled={disabled || code.trim().length === 0}
              onClick={() => void submit()}
            >
              {t('cart.actions.apply')}
            </Button>
          </div>

          {available.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={disabled}>
                  <TicketPercent className="h-3.5 w-3.5" />
                  {t('cart.coupon.available', { count: available.length })}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
                {available.map((coupon) => (
                  <DropdownMenuItem
                    key={coupon.id}
                    onSelect={() => void onApply(coupon.code as string)}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="font-medium">{coupon.code}</span>
                    <span className="text-xs text-muted-foreground">{couponValueLabel(coupon, cart, t)}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {locked && <p className="text-xs text-muted-foreground">{t('cart.coupon.saveFirst')}</p>}
        </div>
      )}
    </section>
  );
}

/**
 * Whether the coupon is worth offering for THIS cart: coded (an automatic promotion applies
 * itself), currently active, and running at the cart's branch. A cart not yet pinned to a branch
 * sees everything — there is no scope to check against, and the confirmation gate re-judges anyway.
 */
function isApplicableTo(coupon: Discount, cart: CartView): boolean {
  if (coupon.code == null) return false;
  if (discountEffectiveState(coupon) !== 'active') return false;
  if (coupon.locationIds?.length && cart.locationId && !coupon.locationIds.includes(cart.locationId)) return false;
  return true;
}

/** What the code is worth, at a glance — the dropdown's second column. */
function couponValueLabel(
  coupon: Discount,
  cart: CartView,
  t: ReturnType<typeof useTranslation<'conversations'>>['t'],
): string {
  if (coupon.type === 'CART_PERCENT') return `-${coupon.value}%`;
  if (coupon.type === 'CART_FIXED') return `-${cart.currencySymbol}${coupon.value.toFixed(2)}`;
  return t('cart.coupon.itemReward');
}
